/**
 * Intelligence page — frontend types mirroring the backend aggregate
 * shapes from `src/services/intelligence/aggregates.ts`.
 *
 * Keep the field names in sync with the server. If a server type
 * changes, update here too.
 */

export type CategoryFamily =
  | "DeFi"
  | "Infra"
  | "AI & DePIN"
  | "Payments"
  | "Tools & Exchange"
  | "Gaming & NFT"
  | "Prediction & Betting"
  | "Privacy & Identity"
  | "SocialFi"
  | "RWA"
  | "Other";

export const CATEGORY_FAMILIES: CategoryFamily[] = [
  "DeFi",
  "Infra",
  "AI & DePIN",
  "Payments",
  "Tools & Exchange",
  "Gaming & NFT",
  "Prediction & Betting",
  "Privacy & Identity",
  "SocialFi",
  "RWA",
  "Other",
];

export type ScoreBand = "40+" | "30-39" | "20-29" | "<20";
export const SCORE_BANDS: ScoreBand[] = ["40+", "30-39", "20-29", "<20"];

export interface IntelligenceKpis {
  totalProjects: number;
  totalCommittedCapitalUsd: number;
  projectsWithFunding: number;
  approachingTge: number;
  medianRaiseUsd: number;
}

export interface CategoryFamilyMatrixCell {
  family: CategoryFamily;
  band: ScoreBand;
  /** Total projects in the family/band (context). */
  projectCount: number;
  /** Projects contributing to the median (funding_raised > 0). */
  fundedCount: number;
  medianRaiseUsd: number | null;
}

export interface ScatterPoint {
  projectId: string;
  name: string;
  twitterHandle: string;
  family: CategoryFamily;
  alphaScore: number;
  fundingRaisedUsd: number;
  residual: number;
}

export interface ScoreFundingScatter {
  points: ScatterPoint[];
  regressionSlope: number;
  regressionIntercept: number;
}

export interface BackerLeagueRow {
  rank: number;
  fundName: string;
  positionCount: number;
  medianPortfolioScore: number | null;
  topFamily: CategoryFamily | null;
}

// ─── v2: Backer specialization matrix ──────────────────────────────

export interface BackerSpecializationFamilyShare {
  /**
   * Category family. Nullable to match `BackerLeagueRow.topFamily`'s
   * contract — `buildFamilyCaseSql` has an `ELSE 'Other'` fallback
   * so a `null` shouldn't surface in practice, but keep the frontend
   * strict so any future schema change surfaces cleanly.
   */
  family: CategoryFamily | null;
  count: number;
  pct: number;
}

export interface BackerSpecializationRow {
  fundName: string;
  totalPositions: number;
  topFamilies: BackerSpecializationFamilyShare[];
}

// ─── v2: Co-investor / deal-comparable finder ───────────────────────

export interface CoInvestorTarget {
  projectId: string;
  /** Nullable — legacy projects can have no display name; UI falls back to twitterHandle. */
  name: string | null;
  twitterHandle: string;
  alphaScore: number;
  fundingRaisedUsd: number | null;
  totalInvestors: number;
}

export interface CoInvestorComparable {
  projectId: string;
  /** Nullable — legacy projects can have no display name; UI falls back to twitterHandle. */
  name: string | null;
  twitterHandle: string;
  alphaScore: number;
  fundingRaisedUsd: number | null;
  /** Nullable for contract parity with `BackerLeagueRow.topFamily`. */
  family: CategoryFamily | null;
  sharedInvestors: string[];
}

export interface CoInvestorFinderResult {
  target: CoInvestorTarget;
  comparables: CoInvestorComparable[];
  /** Total qualifying comparables across the full set (not just the LIMIT-bounded display rows). */
  totalQualifying: number;
  medianComparableRaiseUsd: number | null;
  medianComparableScore: number | null;
}

// ─── v2: Runway stress list ─────────────────────────────────────────

export interface RunwayStressRow {
  projectId: string;
  /** Nullable — legacy projects can have no display name; UI falls back to twitterHandle. */
  name: string | null;
  twitterHandle: string;
  alphaScore: number;
  /** Score delta over the last 30 days; null if no ~30d snapshot available. */
  scoreDelta30d: number | null;
  fundingRaisedUsd: number | null;
  latestRoundStage: string | null;
  /** Unix seconds timestamp of the latest round. */
  latestRoundDate: number;
  daysSinceLatestRound: number;
  family: CategoryFamily | null;
}

// ─── v2 Phase 3: Stealth watchlist ──────────────────────────────────

export interface StealthWatchlistRow {
  projectId: string;
  /** Nullable — legacy projects can have no display name; UI falls back to twitterHandle. */
  name: string | null;
  twitterHandle: string;
  alphaScore: number;
  family: CategoryFamily | null;
  investorCount: number;
  topInvestors: string[];
  fundingRaisedUsd: number | null;
}

// ─── v2 Phase 3: Founder pedigree concentration ─────────────────────

export interface FounderPedigreeBandRow {
  band: ScoreBand;
  totalProjects: number;
  withPedigree: number;
  tier1Count: number;
  tier1Pct: number;
}

export interface FounderPedigreeTopN {
  topN: number;
  totalProjects: number;
  withPedigree: number;
  tier1Count: number;
  tier1Pct: number;
}

export interface FounderPedigreeConcentration {
  byScoreBand: FounderPedigreeBandRow[];
  topProjects: FounderPedigreeTopN;
}

// ─── v2 Phase 3: Ecosystem snapshot ─────────────────────────────────

export interface EcosystemSnapshotRow {
  ecosystem: string;
  projectCount: number;
  totalCommittedUsd: number;
  medianRaiseUsd: number | null;
  medianScore: number | null;
}

// ─── v2 Phase 3: Stage mix ──────────────────────────────────────────

export interface StageMixRow {
  stage: string;
  roundCount: number;
  medianRaiseUsd: number | null;
  medianScore: number | null;
}

export type IntelligenceMode = "operator" | "report";
